import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bitcoin, LayoutDashboard, Globe, Wallet, KeyRound, Receipt, Webhook, Code2, Settings, Users, ScrollText, Activity, LogOut, SlidersHorizontal, Package, Tag, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const merchantNav = [
  { to: "/app", label: "Vue d'ensemble", icon: LayoutDashboard, end: true },
  { to: "/app/orders", label: "Transactions", icon: Receipt },
  { to: "/app/products", label: "Produits & codes", icon: Package },
  { to: "/app/promos", label: "Codes promo", icon: Tag },
  { to: "/app/sites", label: "Sites & domaines", icon: Globe },
  { to: "/app/wallet", label: "Wallet (zpub)", icon: Wallet },
  { to: "/app/api-keys", label: "Clés API", icon: KeyRound },
  { to: "/app/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/app/integration", label: "Intégration", icon: Code2 },
  { to: "/app/settings", label: "Réglages", icon: Settings },
];
const adminNav = [
  { to: "/admin", label: "Statistiques", icon: LayoutDashboard, end: true },
  { to: "/admin/merchants", label: "Marchands", icon: Users },
  { to: "/admin/invites", label: "Invitations", icon: Ticket },
  { to: "/admin/watcher", label: "Santé du watcher", icon: Activity },
  { to: "/admin/logs", label: "Journal d'audit", icon: ScrollText },
  { to: "/admin/settings", label: "Réglages", icon: SlidersHorizontal },
];

export function Layout({ admin = false }) {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const nav_items = admin ? adminNav : merchantNav;
  const mode = user?.merchant?.mode;

  const toggleMode = async (checked) => {
    try {
      await api.patch("/merchant/me", { mode: checked ? "live" : "test" });
      await refresh();
      toast.success(checked ? "Mode LIVE (mainnet) activé" : "Mode TEST (testnet) activé");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-zinc-950/60 backdrop-blur-md sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-border">
          <span className="h-8 w-8 rounded-md bg-amber-500 grid place-items-center text-zinc-950"><Bitcoin size={18} strokeWidth={2.5} /></span>
          <div>
            <div className="font-heading font-bold text-sm tracking-tight">BTC Gateway</div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">non-custodial</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav_items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-${n.to.replace(/\//g, "-").slice(1)}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border text-xs text-zinc-500">
          <div className="truncate text-zinc-300" data-testid="user-email">{user?.email}</div>
          {admin && <Badge className="mt-2 bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid="admin-badge">SUPER-ADMIN</Badge>}
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-14 flex items-center justify-between px-4 sm:px-8 border-b border-border bg-zinc-950/80 backdrop-blur-md" data-testid="header-navbar">
          <div className="flex items-center gap-3 md:hidden">
            <Bitcoin className="text-amber-500" size={20} />
            <span className="font-heading font-bold text-sm">BTC Gateway</span>
          </div>
          <div className="hidden md:block text-sm text-zinc-400 truncate">
            {admin ? "Console super-admin" : <span className="text-zinc-200 font-medium">{user?.merchant?.name}</span>}
          </div>
          <div className="flex items-center gap-4">
            {!admin && (
              <label className="flex items-center gap-2 text-xs font-mono">
                <span className={mode === "test" ? "text-amber-400" : "text-zinc-500"}>TEST</span>
                <Switch checked={mode === "live"} onCheckedChange={toggleMode} data-testid="mode-toggle-switch" />
                <span className={mode === "live" ? "text-emerald-400" : "text-zinc-500"}>LIVE</span>
              </label>
            )}
            <button onClick={async () => { await logout(); nav("/login"); }} className="text-zinc-400 hover:text-white transition-colors" data-testid="logout-button" title="Déconnexion"><LogOut size={16} /></button>
          </div>
        </header>
        <nav className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-border text-xs">
          {nav_items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `px-3 py-1.5 rounded-full whitespace-nowrap ${isActive ? "bg-amber-500 text-zinc-950 font-medium" : "bg-zinc-900 text-zinc-400"}`}>{n.label}</NavLink>
          ))}
        </nav>
        <main className="flex-1 px-4 sm:px-8 py-6 max-w-7xl w-full mx-auto fade-up"><Outlet /></main>
      </div>
    </div>
  );
}
